/**
 * useDocumentCorrection — one per-field lock for "correct what this document is".
 *
 * Lifted out of `DocumentsPage` when the same two selects gained a second home
 * (the intake review on `UploadView`). The guard it carries replaced a
 * value-comparing one that was **wrong four times**, each fix opening the next
 * hole: it remembered the last dispatched value forever, so a value the document
 * was later moved back to became unpickable; then an earlier request's
 * settlement wiped a newer one's marker; then, comparing markers by value, two
 * different dispatches carrying the same value still collided.
 *
 * So there is no value comparison here at all — only "is this field busy". The
 * ref is the correctness guard because it flips in the same tick as the change
 * event; `busyEdits` mirrors it into state only so the control can *render* as
 * disabled. Disabling off the mutation's `isPending` alone would not do: that is
 * React state, and it has the very commit-timing window that made a synchronous
 * latch necessary in the first place.
 *
 * The cost is honest: after changing a field you wait one round trip before you
 * can change that same field again.
 */
import { useRef, useState } from "react";

import { apiErrorMessage } from "./api";
import { useUpdateDocument, type TenderDocRole, type TenderDocument } from "./tenderApi";

export type CorrectableField = "role" | "docKind";

export interface DocumentCorrection {
  /** True while THIS document's THIS field is in flight. */
  isBusy: (docId: string, field: CorrectableField) => boolean;
  /** Correct the pipeline role. Deliberately does NOT clear `needsReview`. */
  changeRole: (doc: TenderDocument, role: TenderDocRole) => Promise<void>;
  /**
   * Correct what KIND of document this is.
   *
   * This is the control that clears the review flag: `needs_review` is a
   * statement about the kind, so only a kind correction can answer it. Changing
   * the pipeline role leaves the flag alone on purpose — otherwise a document
   * would leave the review queue with its actual misclassification untouched.
   *
   * The backend re-derives `role`, `analysis_use`, `kb_ingest`, `kb_depth` and
   * `kb_forward_state` from the taxonomy on a kind change, so a correction here
   * can move a document between groups and make its checkbox appear or vanish.
   * Callers must render from the invalidated query, never from local state.
   */
  changeKind: (doc: TenderDocument, docKind: string) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

export function useDocumentCorrection(projectId: string | null): DocumentCorrection {
  const updateDocument = useUpdateDocument();
  const editing = useRef<Set<string>>(new Set());
  const [busyEdits, setBusyEdits] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  function editKey(docId: string, field: CorrectableField): string {
    return `${docId}:${field}`;
  }

  /** Send one correction, holding that field's lock for exactly its duration. */
  async function correct(
    doc: TenderDocument,
    field: CorrectableField,
    patch: { role?: TenderDocRole; docKind?: string },
  ): Promise<void> {
    if (!projectId) return;
    const key = editKey(doc.id, field);
    if (editing.current.has(key)) return;
    editing.current.add(key);
    setBusyEdits(new Set(editing.current));
    setError(null);
    try {
      await updateDocument.mutateAsync({ id: doc.id, projectId, ...patch });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      editing.current.delete(key);
      setBusyEdits(new Set(editing.current));
    }
  }

  return {
    isBusy: (docId, field) => busyEdits.has(editKey(docId, field)),
    changeRole: async (doc, role) => {
      if (role === doc.role) return;
      await correct(doc, "role", { role });
    },
    changeKind: async (doc, docKind) => {
      if (docKind === doc.docKind) return;
      await correct(doc, "docKind", { docKind });
    },
    error,
    clearError: () => setError(null),
  };
}
